import React, { useContext, useEffect, useState } from 'react';
import { Box, Grid, Typography, Stack, Divider } from '@mui/material';
import axios from 'axios';
import { useGrowSetup } from '../../GrowSetupContext';
import { AuthContext }  from '../../components/signin/AuthContext';
import SensorGauge      from '../components/SensorGauge';
import ActorSwitch      from '../components/ActorSwitch';
import { useV4Resolve } from '../theme';

/* Sensor color palette — each measure gets a unique color */
const SENSORS = [
  { measure:'temperatur',    title:'Temperatur',      unit:'°C',  color:'#ff7c00', ideal:22, max:50  },
  { measure:'temperatur_2',  title:'Temperatur 2',    unit:'°C',  color:'#ff9c44', ideal:22, max:50  },
  { measure:'humidity',      title:'Luftfeuchte',     unit:'%',   color:'#00b4ff', ideal:65, max:100 },
  { measure:'humidity_2',    title:'Luftfeuchte 2',   unit:'%',   color:'#44ccff', ideal:65, max:100 },
  { measure:'co2',           title:'CO₂',             unit:'ppm', color:'#b44fff', ideal:1100,max:2000},
  { measure:'vpd',           title:'VPD',             unit:'kPa', color:'#00ffaa', ideal:0.8, max:3  },
  { measure:'luftdruck',     title:'Luftdruck',       unit:'hPa', color:'#60a0ff', ideal:980, max:1100},
  { measure:'bodenfeuchte_1',title:'Boden 1 Blau',    unit:'%',   color:'#3ecf3e', ideal:60, max:100 },
  { measure:'bodenfeuchte_2',title:'Boden 2 Grün',    unit:'%',   color:'#55e055', ideal:60, max:100 },
  { measure:'bodenfeuchte_3',title:'Boden 3 Gelb',    unit:'%',   color:'#c8e055', ideal:60, max:100 },
  { measure:'bodenfeuchte_4',title:'Boden 4 Rot',     unit:'%',   color:'#e05555', ideal:60, max:100 },
];

const ACTORS = [
  { measure:'licht',   title:'Licht'   },
  { measure:'abluft',  title:'Abluft'  },
  { measure:'heizung', title:'Heizung' },
  { measure:'umluft',  title:'Umluft'  },
];

const DEVICES = [
  { measure:'bme', title:'Sensor BME', unit:'', color:'#a0a0ff', ideal:null, max:1 },
  { measure:'sht', title:'Sensor SHT', unit:'', color:'#ffa0a0', ideal:null, max:1 },
];

function isNowBetween(s, e) {
  if (!s || !e) return true;
  const [sh,sm] = s.split(':').map(Number);
  const [eh,em] = e.split(':').map(Number);
  const now = new Date();
  const start = new Date(now); start.setHours(sh,sm,0,0);
  const end   = new Date(now); end.setHours(eh,em,0,0);
  return start <= end ? now >= start && now <= end : now >= start || now <= end;
}

function SectionLabel({ children }) {
  return (
    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2.5 }}>
      <Typography sx={{ fontSize:'0.62rem', fontWeight:800, textTransform:'uppercase', letterSpacing:'0.16em', color:'rgba(var(--v4-cyan-rgb),0.6)', whiteSpace:'nowrap' }}>
        {children}
      </Typography>
      <Divider sx={{ flex:1, borderColor:'rgba(var(--v4-cyan-rgb),0.08)' }} />
    </Stack>
  );
}

/* Overall health score: average of sensor deviations */
function HealthBanner({ gauges, sensors, sysConfig }) {
  const resolve = useV4Resolve();
  const night   = resolve('#44aaff');
  const scores = sensors.filter(s => s.ideal && gauges[s.measure] && Number(gauges[s.measure]) !== 0).map(s => {
    const dev = Math.abs(Number(gauges[s.measure]) - Number(s.ideal)) / Math.abs(Number(s.ideal));
    return Math.max(0, 1 - dev * 2.5);
  });
  const health = scores.length ? Math.round(scores.reduce((a,b)=>a+b,0)/scores.length*100) : null;
  const hColor = resolve(health == null ? 'var(--v4-neutral)' : health >= 80 ? 'var(--v4-green)' : health >= 60 ? 'var(--v4-orange)' : 'var(--v4-red)');
  const hLabel = health == null ? 'Warte...' : health >= 80 ? 'Gut' : health >= 60 ? 'Abweichend' : 'Kritisch';

  const isDay = sysConfig ? isNowBetween(sysConfig.sun_rise, sysConfig.sun_set) : null;

  return (
    <Box sx={{
      display:'flex', alignItems:'stretch', gap:2, mb:4,
      flexWrap:'wrap',
      animation:'v4FadeUp 0.5s both',
    }}>
      {/* Health score */}
      <Box sx={{
        flex:'1 1 180px', minWidth:160,
        background:'rgba(var(--v4-surface-rgb),0.92)',
        border:`1px solid color-mix(in srgb, ${hColor} 20%, transparent)`,
        borderRadius:'18px', p:2.5,
        display:'flex', alignItems:'center', gap:2,
        boxShadow:`0 4px 28px color-mix(in srgb, ${hColor} 9%, transparent)`,
      }}>
        <Box sx={{ position:'relative', width:56, height:56 }}>
          <svg width={56} height={56} viewBox="0 0 56 56">
            <circle cx={28} cy={28} r={22} fill="none" stroke={resolve('rgba(var(--v4-fg-rgb),0.06)')} strokeWidth={5} />
            <circle cx={28} cy={28} r={22} fill="none" stroke={hColor} strokeWidth={5}
              strokeDasharray={`${(health??0)/100 * 138.23} 138.23`}
              strokeLinecap="round" style={{ transform:'rotate(-90deg)', transformOrigin:'28px 28px', filter:`drop-shadow(0 0 4px ${hColor})` }}
            />
          </svg>
          <Typography sx={{ position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center', fontWeight:800, fontSize:'0.82rem', color:hColor }}>
            {health ?? '—'}
          </Typography>
        </Box>
        <Box>
          <Typography sx={{ fontSize:'0.62rem', color:'rgba(var(--v4-muted-rgb),0.4)', textTransform:'uppercase', letterSpacing:'0.1em' }}>Gesundheit</Typography>
          <Typography sx={{ fontWeight:700, color:hColor, fontSize:'1rem' }}>{hLabel}</Typography>
        </Box>
      </Box>

      {/* Day/Night */}
      {isDay !== null && (
        <Box sx={{
          flex:'1 1 140px', minWidth:120,
          background:'rgba(var(--v4-surface-rgb),0.92)',
          border:`1px solid ${isDay?'color-mix(in srgb, var(--v4-gold) 20%, transparent)':`color-mix(in srgb, ${night} 20%, transparent)`}`,
          borderRadius:'18px', p:2.5,
          display:'flex', alignItems:'center', gap:1.5,
          boxShadow:`0 4px 28px ${isDay?'color-mix(in srgb, var(--v4-gold) 7%, transparent)':`color-mix(in srgb, ${night} 7%, transparent)`}`,
        }}>
          <Typography sx={{ fontSize:26 }}>{isDay ? '☀️' : '🌙'}</Typography>
          <Box>
            <Typography sx={{ fontSize:'0.62rem', color:'rgba(var(--v4-muted-rgb),0.4)', textTransform:'uppercase', letterSpacing:'0.1em' }}>Phase</Typography>
            <Typography sx={{ fontWeight:700, color: isDay?'var(--v4-gold)':night, fontSize:'1rem' }}>{isDay ? 'Tag' : 'Nacht'}</Typography>
          </Box>
        </Box>
      )}

      {/* Sunrise/Sunset */}
      {sysConfig?.sun_rise && (
        <Box sx={{
          flex:'1 1 160px', minWidth:140,
          background:'rgba(var(--v4-surface-rgb),0.92)',
          border:'1px solid rgba(var(--v4-orange-rgb),0.2)',
          borderRadius:'18px', p:2.5,
          display:'flex', alignItems:'center', gap:1.5,
        }}>
          <Typography sx={{ fontSize:22 }}>🌅</Typography>
          <Box>
            <Typography sx={{ fontSize:'0.62rem', color:'rgba(var(--v4-muted-rgb),0.4)', textTransform:'uppercase', letterSpacing:'0.1em' }}>Lichtzeit</Typography>
            <Typography sx={{ fontWeight:700, color:'var(--v4-orange)', fontSize:'0.9rem' }}>
              {sysConfig.sun_rise} – {sysConfig.sun_set}
            </Typography>
          </Box>
        </Box>
      )}

      {/* CO2 quick-read */}
      {gauges.co2 && (
        <Box sx={{
          flex:'1 1 140px', minWidth:120,
          background:'rgba(var(--v4-surface-rgb),0.92)',
          border:'1px solid rgba(var(--v4-purple-rgb),0.25)',
          borderRadius:'18px', p:2.5,
          display:'flex', alignItems:'center', gap:1.5,
        }}>
          <Typography sx={{ fontSize:22 }}>💨</Typography>
          <Box>
            <Typography sx={{ fontSize:'0.62rem', color:'rgba(var(--v4-muted-rgb),0.4)', textTransform:'uppercase', letterSpacing:'0.1em' }}>CO₂</Typography>
            <Typography sx={{ fontWeight:800, color:resolve('#b44fff'), fontSize:'1.1rem', fontFamily:'monospace' }}>
              {gauges.co2} <span style={{ fontSize:'0.65rem', fontWeight:400 }}>ppm</span>
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default function V4DashboardPage() {
  const { grow_setup }  = useGrowSetup();
  const { token }       = useContext(AuthContext);
  const [gauges,    setGauges]    = useState({});
  const [sensors,   setSensors]   = useState(SENSORS);
  const [sysConfig, setSysConfig] = useState(null);

  useEffect(() => {
    if (!grow_setup) return;
    const load = () =>
      axios.post(`/get_current_gauge_values/${grow_setup}`, { hello:'world' }, {
        headers: { Accept:'application/json', 'Content-Type':'application/json', Authorization:`Bearer ${token}` },
      }).then(r => setGauges(r.data || {})).catch(console.error);
    load();
    const id = setInterval(load, 10000);
    return () => clearInterval(id);
  }, [grow_setup]);

  useEffect(() => {
    if (!grow_setup) return;
    axios.post(`/get_sys_config_grow_setup/${grow_setup}`, {}, {
      headers: { Authorization:`Bearer ${token}` },
    }).then(res => {
      const d = res.data;
      setSysConfig(d);
      const day = isNowBetween(d.sun_rise, d.sun_set);
      setSensors(prev => prev.map(s => {
        if (s.measure === 'temperatur' || s.measure === 'temperatur_2')
          return { ...s, ideal: parseFloat(day ? d.temperatur_tag : d.temperatur_nacht) || s.ideal };
        if (s.measure === 'humidity' || s.measure === 'humidity_2')
          return { ...s, ideal: parseFloat(day ? d.luftfeuchte_tag : d.luftfeuchte_nacht) || s.ideal };
        if (s.measure === 'co2')
          return { ...s, ideal: parseFloat(d.co2_zielwert) || s.ideal };
        return s;
      }));
    }).catch(console.error);
  }, [grow_setup]);

  return (
    <Box>
      <style>{`
        @keyframes v4FadeUp { from{opacity:0;transform:translateY(22px)} to{opacity:1;transform:translateY(0)} }
      `}</style>

      {/* Health banner */}
      <HealthBanner gauges={gauges} sensors={sensors} sysConfig={sysConfig} />

      {/* Sensor Gauges */}
      <SectionLabel>Sensoren</SectionLabel>
      <Grid container spacing={2} sx={{ mb: 5 }}>
        {sensors.map((s, i) => (
          <Grid item xs={6} sm={4} md={3} lg={2} key={s.measure}>
            <SensorGauge
              measure={s.measure}
              title={s.title}
              color={s.color}
              unit={s.unit}
              ideal_val={s.ideal}
              gauge_value={gauges[s.measure]}
              animDelay={i * 45}
            />
          </Grid>
        ))}
      </Grid>

      {/* Actor Controls */}
      <SectionLabel>Aktoren</SectionLabel>
      <Grid container spacing={2} sx={{ mb: 5 }}>
        {ACTORS.map((a, i) => (
          <Grid item xs={12} sm={6} md={3} key={a.measure}>
            <ActorSwitch measure={a.measure} title={a.title} animDelay={i * 60} />
          </Grid>
        ))}
      </Grid>

      {/* Devices */}
      <SectionLabel>Geräte</SectionLabel>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {DEVICES.map((d, i) => (
          <Grid item xs={6} sm={4} md={3} lg={2} key={d.measure}>
            <SensorGauge
              measure={d.measure}
              title={d.title}
              color={d.color}
              unit={d.unit}
              ideal_val={null}
              gauge_value={gauges[d.measure]}
              animDelay={i * 60}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}
